/**
 * ShareChangesBanner.jsx : Retour après enregistrement de la fiche cheval
 * quand des partages ont été créés ou retirés (10.5.306, D231).
 *
 * Pourquoi : la fiche se ferme dès l'enregistrement ; sans ce bandeau, le
 * propriétaire ne savait pas qu'une invitation était partie. La fiche peut
 * s'ouvrir depuis la liste comme depuis la vue du cheval : le bandeau est
 * monté par chaque shell en tête de contenu, et reste affiché jusqu'à ce que
 * l'utilisateur le ferme.
 *
 * useShareChangesFeedback() : état du bandeau + onHorseUpdated, à passer en
 * onUpdate de HorseEditModal à la place de handleHorseUpdated.
 */
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';

const hasChanges = (changes) => Boolean(changes)
  && ['invited', 'revoked', 'limitSkipped'].some((key) => (changes[key] || []).length > 0);

export function useShareChangesFeedback(handleHorseUpdated) {
  const [feedback, setFeedback] = useState(null);

  const onHorseUpdated = useCallback((horse, shareChanges) => {
    handleHorseUpdated(horse);
    if (hasChanges(shareChanges)) setFeedback({ horseName: horse?.horseName || '', changes: shareChanges });
  }, [handleHorseUpdated]);

  const dismiss = useCallback(() => setFeedback(null), []);
  return { feedback, onHorseUpdated, dismiss };
}

export function ShareChangesBanner({ feedback, dismiss, darkMode }) {
  const { t } = useTranslation();
  if (!feedback) return null;
  const { invited = [], revoked = [], limitSkipped = [] } = feedback.changes;
  const anyFailure = invited.some((i) => !i.emailSent) || limitSkipped.length > 0;
  const accent = anyFailure ? '#F59E0B' : '#10B981';

  const lines = [
    ...invited.map((i) => (i.emailSent
      ? t('horses.shareChanges.invited', { email: i.email })
      : t('horses.shareChanges.invitedEmailFailed', { email: i.email }))),
    ...revoked.map((r) => t('horses.shareChanges.revoked', { email: r.email })),
    ...limitSkipped.map((l) => t('horses.shareChanges.limitSkipped', { email: l.email })),
  ];

  return (
    <div role="status" data-testid="share-changes-banner"
      style={{
        padding: '12px 16px', borderRadius: '8px', marginBottom: 16,
        background: darkMode ? 'rgba(16, 185, 129, 0.12)' : '#ECFDF5',
        color: darkMode ? '#E1E1E1' : '#1a1a2e',
        border: `1px solid ${accent}`,
        display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12,
      }}>
      <div>
        <strong>{t('horses.shareChanges.title', { horse: feedback.horseName })}</strong>
        <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
          {lines.map((line) => <li key={line}>{line}</li>)}
        </ul>
      </div>
      <button type="button" onClick={dismiss} aria-label={t('horses.shareChanges.dismiss')}
        style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'inherit', padding: 4 }}>
        <X size={16} />
      </button>
    </div>
  );
}
